import type { GeneratorSizingResponse } from "../services/generatorSizing";
import type { GeneratorSizingWarningCode } from "../services/generatorSizingContract";
import { EMPTY_QUANTITY, formatQuantity } from "../utils/formatQuantity";
import { REDUNDANCY_LABELS, STATUS_LABELS } from "./GeneratorResultSummary";
import { DUTY_CLASS_LABELS } from "./GeneratorStudyForm";

interface ValueRow {
  key: string;
  label: string;
  // Decimal values are exact backend strings: four significant figures on
  // screen, the exact value in the tooltip (display rule A12). The unit is in
  // the row label. null renders as an em dash.
  value: string | null;
  // The warnings that say this figure was assumed rather than established. A
  // single input names its own code; the combined derating factor names both,
  // because either one makes the product an assumption too.
  notEstablishedCodes?: readonly GeneratorSizingWarningCode[];
  // What follows the figure when one of those warnings is present.
  assumedSuffix?: string;
}

interface GeneratorResultPanelProps {
  result: GeneratorSizingResponse;
}

const NOT_ESTABLISHED_SUFFIX = " (not established)";
const INCLUDES_NOT_ESTABLISHED_SUFFIX = " (includes a not-established factor)";

/**
 * The loading figure is not the running load over the nameplate: it is the
 * steady-state design requirement, growth and margin included, over the
 * derated installed duty capacity. The label says so in full.
 */
export const LOADING_LABEL =
  "Loading (steady-state design requirement incl. growth and margin ÷ derated installed duty capacity)";

// The two factors the combined derating factor is the product of.
const DERATING_CODES = [
  "AMBIENT_DERATING_NOT_ESTABLISHED",
  "ALTITUDE_DERATING_NOT_ESTABLISHED",
] as const satisfies readonly GeneratorSizingWarningCode[];

// The calculation ladder, in the engine's order: demand, growth and margin to the
// steady-state requirement, the step load to the transient requirement, the
// larger of the two, and derating to the nameplate the schedule must cover.
function demandRows(result: GeneratorSizingResponse): ValueRow[] {
  return [
    {
      key: "steady_state_demand_kw",
      label: "Steady-state demand (kW)",
      value: result.steady_state_demand_kw,
    },
    {
      key: "steady_state_power_factor",
      label: "Power factor",
      value: result.steady_state_power_factor,
    },
    {
      key: "steady_state_demand_kva",
      label: "Apparent demand, kW ÷ power factor (kVA)",
      value: result.steady_state_demand_kva,
    },
    {
      key: "future_growth_factor",
      label: "Future growth factor",
      value: result.future_growth_factor,
      notEstablishedCodes: ["GROWTH_FACTOR_NOT_ESTABLISHED"],
    },
    {
      key: "future_steady_state_kva",
      label: "After growth (kVA)",
      value: result.future_steady_state_kva,
    },
    {
      key: "design_margin_factor",
      label: "Design margin factor",
      value: result.design_margin_factor,
      notEstablishedCodes: ["DESIGN_MARGIN_NOT_ESTABLISHED"],
    },
    {
      key: "steady_state_required_kva",
      label: "Steady-state design requirement (kVA)",
      value: result.steady_state_required_kva,
    },
    {
      key: "transient_step_load_kva",
      label: "Step load (kVA)",
      value: result.transient_step_load_kva,
      notEstablishedCodes: ["STEP_LOAD_NOT_ESTABLISHED"],
    },
    {
      key: "transient_allowance_factor",
      label: "Transient allowance factor",
      value: result.transient_allowance_factor,
      notEstablishedCodes: ["TRANSIENT_ALLOWANCE_NOT_ESTABLISHED"],
    },
    {
      key: "transient_additional_kva",
      label: "Transient addition, step load × allowance (kVA)",
      value: result.transient_additional_kva,
    },
    {
      key: "transient_required_kva",
      label: "Transient requirement, after growth + transient addition (kVA)",
      value: result.transient_required_kva,
    },
    {
      key: "governing_required_kva",
      label: "Governing requirement (kVA)",
      value: result.governing_required_kva,
    },
    {
      key: "combined_derating_factor",
      label: "Combined derating factor",
      value: result.combined_derating_factor,
      notEstablishedCodes: DERATING_CODES,
      assumedSuffix: INCLUDES_NOT_ESTABLISHED_SUFFIX,
    },
    {
      key: "required_nameplate_capacity_kva",
      label: "Required nameplate capacity (kVA)",
      value: result.required_nameplate_capacity_kva,
    },
  ];
}

function capacityRows(result: GeneratorSizingResponse): ValueRow[] {
  return [
    {
      key: "installed_nameplate_capacity_kva",
      label: "Installed capacity (kVA)",
      value: result.installed_nameplate_capacity_kva,
    },
    {
      key: "derated_duty_capacity_kva",
      label: "Derated installed duty capacity (kVA)",
      value: result.derated_duty_capacity_kva,
    },
    {
      key: "spare_derated_capacity_kva",
      label: "Spare derated capacity (kVA)",
      value: result.spare_derated_capacity_kva,
    },
  ];
}

function ValueTable({
  title,
  rows,
  warningCodes,
}: {
  title: string;
  rows: ValueRow[];
  warningCodes: ReadonlySet<string>;
}) {
  return (
    <section aria-label={title}>
      <h3>{title}</h3>
      <div data-table-scroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Parameter</th>
              <th scope="col">Value</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const quantity = formatQuantity(row.value);
              // An input is always a figure; only the warning says whether the
              // engineer established it (A16 (a), A17). The tooltip keeps the
              // exact value either way.
              const assumed =
                row.notEstablishedCodes?.some((code) => warningCodes.has(code)) ?? false;
              const suffix = row.assumedSuffix ?? NOT_ESTABLISHED_SUFFIX;
              return (
                <tr key={row.key} data-row-key={row.key}>
                  <th scope="row">{row.label}</th>
                  <td title={quantity.exact ?? undefined}>
                    {assumed ? `${quantity.display}${suffix}` : quantity.display}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function GeneratorResultPanel({ result }: GeneratorResultPanelProps) {
  const warningCodes = new Set<string>(result.warnings.map((warning) => warning.code));
  // The long label leaves no room for a unit, so the percent sign stays with the figure.
  const loading = formatQuantity(result.steady_state_loading_percent, "%");

  return (
    <article aria-label="Generator sizing result">
      <header>
        <h2>Generator sizing result</h2>
        <p>
          Study code: <strong data-study-code="true">{result.code}</strong>
          {" - "}
          {result.name}
        </p>
        <p>
          Overall status:{" "}
          <strong data-result-status={result.status}>{STATUS_LABELS[result.status]}</strong>
        </p>
      </header>

      <ValueTable
        title="Demand and design"
        rows={demandRows(result)}
        warningCodes={warningCodes}
      />

      <section aria-label="Selection">
        <h3>Selection</h3>
        <div data-table-scroll>
          <table>
            <thead>
              <tr>
                <th scope="col">Parameter</th>
                <th scope="col">Value</th>
              </tr>
            </thead>
            <tbody>
              <tr data-row-key="required_unit_rating_kva">
                <th scope="row">Required unit rating, after derating (kVA)</th>
                <td title={formatQuantity(result.required_unit_rating_kva).exact ?? undefined}>
                  {formatQuantity(result.required_unit_rating_kva).display}
                </td>
              </tr>
              <tr data-row-key="selected_unit_rating_kva">
                <th scope="row">Selected unit rating (kVA)</th>
                {/* A catalogue value from the project design basis: shown as
                    sent, so the engineer reads back an orderable rating. */}
                <td>{result.selected_unit_rating_kva ?? EMPTY_QUANTITY}</td>
              </tr>
              <tr data-row-key="duty_units">
                <th scope="row">Duty units</th>
                {/* Counts, not engineering quantities. */}
                <td>{result.duty_units}</td>
              </tr>
              <tr data-row-key="standby_units">
                <th scope="row">Standby units</th>
                <td>{result.standby_units}</td>
              </tr>
              <tr data-row-key="total_units">
                <th scope="row">Total units</th>
                <td>{result.total_units}</td>
              </tr>
              <tr data-row-key="redundancy_mode">
                <th scope="row">Redundancy mode</th>
                <td data-redundancy-mode={result.redundancy_mode}>
                  {REDUNDANCY_LABELS[result.redundancy_mode]}
                </td>
              </tr>
              <tr data-row-key="duty_class">
                <th scope="row">Duty class</th>
                <td data-duty-class={result.duty_class}>
                  {DUTY_CLASS_LABELS[result.duty_class]}
                </td>
              </tr>
              {capacityRows(result).map((row) => {
                const quantity = formatQuantity(row.value);
                return (
                  <tr key={row.key} data-row-key={row.key}>
                    <th scope="row">{row.label}</th>
                    <td title={quantity.exact ?? undefined}>{quantity.display}</td>
                  </tr>
                );
              })}
              <tr data-row-key="steady_state_loading_percent">
                <th scope="row">{LOADING_LABEL}</th>
                <td title={loading.exact ?? undefined}>{loading.display}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section aria-label="Jurisdiction profile">
        <h3>Jurisdiction profile</h3>
        <p data-jurisdiction-profile={result.jurisdiction_profile}>
          Designed under the {result.jurisdiction_profile} profile. The sizing itself carries
          no profile-dependent value; the profile is recorded with the run and must match the
          design basis of the project revision.
        </p>
      </section>

      <footer>
        <p>
          Calculation results are engineering design checks, not a statutory compliance
          declaration. Confirm the governing project basis, the demand data, the step load,
          the rating schedule and every growth, margin, allowance and derating factor before
          approval or issue. Voltage and frequency dip on load steps are not evaluated.
        </p>
      </footer>
    </article>
  );
}

export default GeneratorResultPanel;
